import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Lock, Database, UserCheck, Mail } from "lucide-react";

const PrivacyPolicy = () => {
  const lastUpdated = "January 10, 2026";

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8 md:py-12" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <Card className="shadow-lg">
          <CardHeader className="border-b">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-[#2c3e50]" />
              <div>
                <CardTitle className="text-3xl font-bold text-[#2c3e50] font-['Manrope']">
                  Privacy Policy
                </CardTitle>
                <p className="text-[#546e7a]">Last updated: {lastUpdated}</p>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-6 md:p-8 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                Introduction
              </h2>
              <p className="text-base text-[#546e7a] leading-relaxed">
                Protect Elders from Fraud (PEFF) is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, store, and protect your data when you use our platform.
              </p>
            </section>

            {/* Data Collection */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-6 h-6 text-[#2c3e50]" />
                <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope']">
                  What Data We Collect
                </h2>
              </div>
              <div className="bg-slate-50 rounded-lg p-6 space-y-4">
                <div>
                  <h3 className="font-semibold text-[#2c3e50] mb-2">Personal Information</h3>
                  <ul className="list-disc list-inside text-[#546e7a] space-y-1">
                    <li>Name, age, city, state, and phone number</li>
                    <li>Details about the scam incident</li>
                    <li>Financial information (amount lost, bank name, transaction method)</li>
                    <li>Legal documents (FIR copies, screenshots)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-[#2c3e50] mb-2">Technical Information</h3>
                  <ul className="list-disc list-inside text-[#546e7a] space-y-1">
                    <li>Browser type and version</li>
                    <li>Device information</li>
                    <li>IP address (for security purposes only)</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Data */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <UserCheck className="w-6 h-6 text-[#2c3e50]" />
                <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope']">
                  How We Use Your Data
                </h2>
              </div>
              <p className="text-base text-[#546e7a] leading-relaxed mb-4">
                We collect data solely to assist victims of financial fraud. Your information is used to:
              </p>
              <ul className="list-disc list-inside text-[#546e7a] space-y-2">
                <li>Create and track your fraud report case</li>
                <li>Verify documents and validate claims</li>
                <li>Provide status updates on your case</li>
                <li>Generate statistics (anonymized) for public awareness</li>
                <li>Improve our platform and services</li>
              </ul>
            </section>

            {/* Data Security */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-6 h-6 text-[#2c3e50]" />
                <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope']">
                  Data Security
                </h2>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <ul className="space-y-3 text-[#2c3e50]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#27ae60] font-bold">✓</span>
                    FIRs and sensitive financial data are encrypted at rest and in transit
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#27ae60] font-bold">✓</span>
                    Access to personal data is restricted to authorized personnel only
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#27ae60] font-bold">✓</span>
                    We do NOT sell data to third parties under any circumstances
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#27ae60] font-bold">✓</span>
                    Regular security audits are conducted to ensure data protection
                  </li>
                </ul>
              </div>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                Data Retention
              </h2>
              <p className="text-base text-[#546e7a] leading-relaxed">
                We retain your data for as long as necessary to provide our services and comply with legal obligations. Case data is typically retained for 7 years after case closure for legal and audit purposes. You may request deletion of your data at any time by contacting us.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                Your Rights
              </h2>
              <p className="text-base text-[#546e7a] leading-relaxed mb-4">
                Under the Indian Digital Personal Data Protection principles, you have the right to:
              </p>
              <ul className="list-disc list-inside text-[#546e7a] space-y-2">
                <li>Access your personal data stored with us</li>
                <li>Correct inaccurate or incomplete data</li>
                <li>Request deletion of your data (subject to legal requirements)</li>
                <li>Withdraw consent for data processing</li>
                <li>Receive your data in a portable format</li>
              </ul>
            </section>

            {/* Contact */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-6 h-6 text-[#2c3e50]" />
                <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope']">
                  Contact Us
                </h2>
              </div>
              <p className="text-base text-[#546e7a] leading-relaxed">
                For any privacy-related queries or to exercise your rights, please contact our Data Protection Officer at{" "}
                <a href="mailto:privacy@peff.org" className="text-[#2c3e50] font-semibold underline">
                  privacy@peff.org
                </a>
              </p>
            </section>

            {/* Compliance */}
            <section className="bg-slate-50 rounded-lg p-6">
              <h2 className="text-xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                Compliance
              </h2>
              <p className="text-base text-[#546e7a] leading-relaxed">
                This Privacy Policy is compliant with the Indian Digital Personal Data Protection Act (DPDPA) and Information Technology Act, 2000. We are committed to maintaining the highest standards of data protection and privacy.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
